import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { MonkeyLogo } from '../components/MonkeyLogo';
import {
  ArrowLeft, Users, MessageSquare, Send, Loader2,
  ChevronDown, ChevronUp, ExternalLink, Link as LinkIcon, Gift, DollarSign, Sparkles, Wallet, Download, Check, X, Shield, Clock, RefreshCw, TrendingUp, Mail, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { api } from '../utils/api';
import { AdminNudgesTab } from '../components/admin/AdminNudgesTab';
import { AdminReviewsTab } from '../components/admin/AdminReviewsTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminMessagesTab } from '../components/admin/AdminMessagesTab';
import { AdminReferralsTab } from '../components/admin/AdminReferralsTab';
import { AdminAffiliatesTab } from '../components/admin/AdminAffiliatesTab';
import { AdminPayoutsTab } from '../components/admin/AdminPayoutsTab';
import { AuditPanel } from '../components/admin/AuditPanel';
import { AdminTrafficTab } from '../components/admin/AdminTrafficTab';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedThread, setExpandedThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const replyEndRef = useRef(null);
  const [referralStats, setReferralStats] = useState(null);
  const [affiliateApps, setAffiliateApps] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [replayingWebhooks, setReplayingWebhooks] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [grantingProId, setGrantingProId] = useState(null);
  const [syncingStripeId, setSyncingStripeId] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [loadingTraffic, setLoadingTraffic] = useState(false);
  const [nudges, setNudges] = useState(null);
  const [loadingNudges, setLoadingNudges] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [loadingReviews, setLoadingReviews] = useState(false);

  const loadReviews = async () => {
    setLoadingReviews(true);
    try { setReviews((await api.adminReviews()).reviews); }
    catch (e) { toast.error(e.message); }
    finally { setLoadingReviews(false); }
  };

  const handleReviewAction = async (id, action) => {
    try {
      await api.adminReviewAction(id, action);
      toast.success(action === 'approve' ? 'Review approved' : 'Review rejected');
      loadReviews();
    } catch (e) { toast.error(e.message); }
  };
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [diagnoseUsername, setDiagnoseUsername] = useState('');
  const [diagnoseResult, setDiagnoseResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const handleDiagnoseAffiliate = async () => {
    const name = diagnoseUsername.trim().replace(/^@/, '');
    if (!name) {
      toast.error('Enter a username');
      return;
    }
    setDiagnosing(true);
    setDiagnoseResult(null);
    try {
      const res = await api.adminDiagnoseAffiliate(name);
      setDiagnoseResult(res);
      const blockers = res.eligibility?.blockers || [];
      const missing = res.summary?.commissions_missing || 0;
      const recorded = res.summary?.commissions_recorded || 0;
      if (blockers.length === 0 && missing === 0 && recorded > 0) {
        toast.success(`@${name} earning correctly — ${recorded} commission(s) recorded`);
      } else if (blockers.length > 0) {
        toast.warning(`@${name} is BLOCKED: ${blockers[0]}`, { duration: 10000 });
      } else if (missing > 0) {
        toast.warning(`@${name}: ${missing} paid invoice(s) without commission. See breakdown.`, { duration: 10000 });
      } else {
        toast.info(`@${name}: ${res.summary?.referrals_total || 0} referral(s), no payments yet.`);
      }
      console.log('[Affiliate Diagnose]', res);
    } catch (e) {
      toast.error(e.message || 'Diagnostic failed');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleGrantPro = async (userId, currentlyPro) => {
    const cycle = currentlyPro
      ? null
      : window.prompt('Grant cycle? Type: monthly / yearly / lifetime', 'lifetime');
    if (!currentlyPro && !cycle) return;
    if (!currentlyPro && !['monthly', 'yearly', 'lifetime'].includes(cycle.trim())) {
      toast.error('Invalid cycle');
      return;
    }
    if (currentlyPro && !window.confirm('Revoke Pro access for this user?')) return;
    setGrantingProId(userId);
    try {
      const res = await api.adminGrantPro(userId, currentlyPro ? null : cycle.trim(), currentlyPro ? 'revoke' : 'grant');
      toast.success(res.message);
      setUsers(prev => prev.map(u => u.id === userId
        ? { ...u, plan: res.plan, billing_cycle: res.billing_cycle, subscription_ends_at: res.subscription_ends_at, is_pro: res.plan === 'pro' }
        : u));
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setGrantingProId(null);
    }
  };

  const handleReplayFailedWebhooks = async () => {
    setReplayingWebhooks(true);
    try {
      const res = await api.adminReplayFailedWebhooks();
      const summary = `Replayed ${res.replayed_count} · Still failing ${res.still_failing_count}`;
      if (res.replayed_count > 0) {
        toast.success(summary, { duration: 8000 });
        try { setUsers(await api.adminGetUsers()); } catch (_) { /* non-fatal */ }
      } else {
        toast.info(summary, { duration: 6000 });
      }
      if (res.still_failing?.length) {
        console.warn('[Replay Webhooks] Still failing after retry:', res.still_failing);
      }
    } catch (e) {
      toast.error(e.message || 'Replay failed');
    } finally {
      setReplayingWebhooks(false);
    }
  };

  const handleStripeReconcile = async () => {
    if (!window.confirm('Walk every active Stripe subscription and fix any users still showing as Free? Safe to run repeatedly.')) return;
    setReconciling(true);
    try {
      const res = await api.adminStripeReconcile();
      const summary = `Subs: scanned ${res.scanned} · fixed ${res.fixed_count} · synced ${res.already_synced_count} · orphans ${res.orphan_count}` +
        (res.invoices_scanned !== undefined
          ? ` · Commissions: scanned ${res.invoices_scanned} invoices, backfilled ${res.commissions_backfilled_count}`
          : '');
      if (res.fixed_count > 0 || res.commissions_backfilled_count > 0) {
        toast.success(summary, { duration: 10000 });
        try { setUsers(await api.adminGetUsers()); } catch (_) { /* non-fatal */ }
      } else {
        toast.info(summary, { duration: 8000 });
      }
      // Surface the diagnostic breakdown when no commissions were backfilled,
      // so admin can immediately see WHY (no buyer match / gate blocked / etc.)
      if (res.commissions_backfilled_count === 0 && res.invoices_scanned > 0) {
        const breakdown = [
          `${res.invoices_skipped_already_present || 0} already had a commission`,
          `${res.invoices_skipped_no_buyer || 0} have no matching LinkMonkey user`,
          `${res.invoices_skipped_no_plan || 0} have no recognised plan`,
          `${res.invoices_skipped_gate_blocked || 0} blocked by Pro/PayPal gate`,
        ].join(' · ');
        toast.info(`Why no backfill? ${breakdown}`, { duration: 14000 });
      }
      if (res.orphans?.length) {
        console.log('[Stripe Reconcile] Orphan subscriptions (paid but no LinkMonkey account):', res.orphans);
      }
      if (res.commissions_backfilled?.length) {
        console.log('[Stripe Reconcile] Backfilled commissions:', res.commissions_backfilled);
      }
      if (res.errors?.length) {
        console.warn('[Stripe Reconcile] Per-subscription errors:', res.errors);
      }
      if (res.commission_errors?.length) {
        console.warn('[Stripe Reconcile] Gate-blocked commissions (why):', res.commission_errors);
      }
    } catch (e) {
      toast.error(e.message || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  const handleSyncStripe = async (userId, username) => {
    setSyncingStripeId(userId);
    try {
      const res = await api.adminSyncStripeUser(userId);
      if (res.ok && res.plan) {
        toast.success(`@${username} synced to Pro (${res.plan})`);
        setUsers(prev => prev.map(u => u.id === userId
          ? { ...u, plan: 'pro', billing_cycle: res.plan, subscription_ends_at: res.ends_at, is_pro: true }
          : u));
      } else if (res.ok && res.active === false) {
        toast.warning(`No active Stripe subscription found for @${username}.`);
      } else {
        toast.error(res.error || 'Sync failed');
      }
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setSyncingStripeId(null);
    }
  };

  const handleMarkPaid = async (userId, amount) => {
    if (!window.confirm(`Mark €${amount.toFixed(2)} as paid out to this affiliate? This cannot be undone.`)) return;
    setMarkingPaidId(userId);
    try {
      const note = window.prompt('Optional note (e.g. PayPal txn ID):', '') || '';
      const res = await api.adminMarkPaid(userId, note);
      toast.success(res.message);
      // Refresh both views so the Referrals + Payouts tabs reflect the change
      try {
        const [payoutsUpdated, refsUpdated, historyUpdated] = await Promise.all([
          api.adminPayoutsDue().catch(() => null),
          api.adminGetReferrals().catch(() => null),
          api.adminPayoutsHistory({ limit: 200 }).catch(() => null),
        ]);
        if (payoutsUpdated) setPayouts(payoutsUpdated);
        if (refsUpdated) setReferralStats(refsUpdated);
        if (historyUpdated) setPayoutHistory(historyUpdated);
      } catch (_) { /* non-fatal */ }
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setMarkingPaidId(null);
    }
  };

  const exportPayoutsCSV = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/admin/payouts/export`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = 'linkmonkey-payouts-due.csv'; link.click();
      URL.revokeObjectURL(url);
      toast.success('Payouts CSV downloaded');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  const exportPayoutHistoryCSV = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/admin/payouts/history/export`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = 'linkmonkey-payout-history.csv'; link.click();
      URL.revokeObjectURL(url);
      toast.success('Payout history CSV downloaded');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  const handleToggleAffiliate = async (userId, currentlyAffiliate) => {
    setTogglingId(userId);
    try {
      const res = await api.adminToggleAffiliate(userId);
      toast.success(res.message || (res.is_affiliate ? 'Affiliate enabled' : 'Affiliate revoked'));
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_affiliate: res.is_affiliate } : u));
    } catch (e) {
      toast.error(e.message || 'Toggle failed');
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.email !== 'admin@linkmonkey.io') {
      navigate('/dashboard');
      return;
    }
    loadData();
    // Auto-poll every 15 seconds
    const interval = setInterval(() => {
      api.adminGetMessages().then(res => setThreads(res.threads || [])).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, msgsRes] = await Promise.all([
        api.adminGetUsers(),
        api.adminGetMessages()
      ]);
      setUsers(usersRes.users || []);
      setThreads(msgsRes.threads || []);
      api.adminGetReferrals().then(setReferralStats).catch(() => {});
      api.adminGetAffiliates().then(setAffiliateApps).catch(() => {});
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandThread = async (userId) => {
    if (expandedThread === userId) {
      setExpandedThread(null);
      return;
    }
    setExpandedThread(userId);
    // Mark as read when admin opens the thread
    const thread = threads.find(t => t.user_id === userId);
    if (thread?.has_unread_admin) {
      await api.adminMarkRead(userId).catch(() => {});
      setThreads(prev => prev.map(t => t.user_id === userId ? { ...t, has_unread_admin: false } : t));
    }
  };

  const handleReply = async (userId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api.adminReply(userId, replyText.trim());
      setReplyText('');
      const msgsRes = await api.adminGetMessages();
      setThreads(msgsRes.threads || []);
      toast.success('Reply sent');
      setTimeout(() => replyEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!user || (user.role !== 'admin' && user.email !== 'admin@linkmonkey.io')) return null;

  return (
    <div className="min-h-screen bg-gradient-subtle" data-testid="admin-page">
      <nav className="bg-background/80 backdrop-blur-lg border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" data-testid="admin-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
              </Link>
              <div className="flex items-center space-x-2">
                <MonkeyLogo size={32} />
                <span className="text-xl font-bold">Admin</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant={tab === 'users' ? 'default' : 'outline'} size="sm" onClick={() => setTab('users')} data-testid="tab-users">
                <Users className="w-4 h-4 mr-2" /> Users ({users.length})
              </Button>
              <Button variant={tab === 'messages' ? 'default' : 'outline'} size="sm" onClick={() => setTab('messages')} data-testid="tab-messages">
                <MessageSquare className="w-4 h-4 mr-2" /> Messages ({threads.length})
                {threads.filter(t => t.has_unread_admin).length > 0 && (
                  <span className="ml-2 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center">
                    {threads.filter(t => t.has_unread_admin).length}
                  </span>
                )}
              </Button>
              <Button variant={tab === 'referrals' ? 'default' : 'outline'} size="sm" onClick={() => setTab('referrals')} data-testid="tab-referrals">
                <Gift className="w-4 h-4 mr-2" /> Referrals ({referralStats?.total_referrals ?? 0})
              </Button>
              <Button variant={tab === 'affiliates' ? 'default' : 'outline'} size="sm" onClick={() => setTab('affiliates')} data-testid="tab-affiliates">
                <DollarSign className="w-4 h-4 mr-2" /> Affiliates
                {affiliateApps?.stats?.pending > 0 && (
                  <span className="ml-2 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] flex items-center justify-center">
                    {affiliateApps.stats.pending}
                  </span>
                )}
              </Button>
              <Button variant={tab === 'payouts' ? 'default' : 'outline'} size="sm" onClick={async () => {
                setTab('payouts');
                try {
                  if (!payouts) setPayouts(await api.adminPayoutsDue());
                  setLoadingHistory(true);
                  setPayoutHistory(await api.adminPayoutsHistory({ limit: 200 }));
                } catch (e) { toast.error(e.message); }
                finally { setLoadingHistory(false); }
              }} data-testid="tab-payouts">
                <Wallet className="w-4 h-4 mr-2" /> Payouts
                {payouts?.total_due > 0 && (
                  <span className="ml-2 text-[10px] font-semibold">€{payouts.total_due.toFixed(2)}</span>
                )}
              </Button>
              <Button variant={tab === 'traffic' ? 'default' : 'outline'} size="sm" onClick={async () => {
                setTab('traffic');
                setLoadingTraffic(true);
                try { setTraffic(await api.adminTraffic()); }
                catch (e) { toast.error(e.message); }
                finally { setLoadingTraffic(false); }
              }} data-testid="tab-traffic">
                <TrendingUp className="w-4 h-4 mr-2" /> Traffic
              </Button>
              <Button variant={tab === 'nudges' ? 'default' : 'outline'} size="sm" onClick={async () => {
                setTab('nudges');
                setLoadingNudges(true);
                try { setNudges(await api.adminNudgeInsights()); }
                catch (e) { toast.error(e.message); }
                finally { setLoadingNudges(false); }
              }} data-testid="tab-nudges">
                <Mail className="w-4 h-4 mr-2" /> Nudges
              </Button>
              <Button variant={tab === 'reviews' ? 'default' : 'outline'} size="sm" onClick={() => { setTab('reviews'); loadReviews(); }} data-testid="tab-reviews">
                <Star className="w-4 h-4 mr-2" /> Reviews
              </Button>
              <Button variant={tab === 'audit' ? 'default' : 'outline'} size="sm" onClick={async () => {
                setTab('audit');
                if (!auditLog) {
                  setLoadingAudit(true);
                  try { setAuditLog(await api.adminAuditLog({ limit: 100 })); }
                  catch (e) { toast.error(e.message); }
                  finally { setLoadingAudit(false); }
                }
              }} data-testid="tab-audit">
                <Shield className="w-4 h-4 mr-2" /> Activity
                {auditLog?.total > 0 && (
                  <span className="ml-2 text-[10px] font-semibold opacity-80">{auditLog.total}</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : tab === 'users' ? (
          <AdminUsersTab
            users={users}
            replayingWebhooks={replayingWebhooks}
            handleReplayFailedWebhooks={handleReplayFailedWebhooks}
            reconciling={reconciling}
            handleStripeReconcile={handleStripeReconcile}
            grantingProId={grantingProId}
            handleGrantPro={handleGrantPro}
            togglingId={togglingId}
            handleToggleAffiliate={handleToggleAffiliate}
            syncingStripeId={syncingStripeId}
            handleSyncStripe={handleSyncStripe}
          />
        ) : tab === 'messages' ? (
          <AdminMessagesTab
            threads={threads}
            expandedThread={expandedThread}
            handleExpandThread={handleExpandThread}
            replyText={replyText}
            setReplyText={setReplyText}
            sending={sending}
            handleReply={handleReply}
            replyEndRef={replyEndRef}
          />
        ) : tab === 'referrals' ? (
          <AdminReferralsTab
            referralStats={referralStats}
            diagnoseUsername={diagnoseUsername}
            setDiagnoseUsername={setDiagnoseUsername}
            diagnosing={diagnosing}
            diagnoseResult={diagnoseResult}
            handleDiagnoseAffiliate={handleDiagnoseAffiliate}
            markingPaidId={markingPaidId}
            handleMarkPaid={handleMarkPaid}
          />
        ) : tab === 'affiliates' ? (
          <AdminAffiliatesTab affiliateApps={affiliateApps} setAffiliateApps={setAffiliateApps} />
        ) : tab === 'payouts' ? (
          <AdminPayoutsTab
            payouts={payouts}
            payoutHistory={payoutHistory}
            loadingHistory={loadingHistory}
            markingPaidId={markingPaidId}
            handleMarkPaid={handleMarkPaid}
            exportPayoutsCSV={exportPayoutsCSV}
            exportPayoutHistoryCSV={exportPayoutHistoryCSV}
          />
        ) : tab === 'traffic' ? (
          <AdminTrafficTab traffic={traffic} loading={loadingTraffic} />
        ) : tab === 'nudges' ? (
          <AdminNudgesTab nudges={nudges} loading={loadingNudges} />
        ) : tab === 'reviews' ? (
          <AdminReviewsTab reviews={reviews} loading={loadingReviews} onAction={handleReviewAction} />
        ) : tab === 'audit' ? (
          <AuditPanel
            auditLog={auditLog}
            loading={loadingAudit}
            onRefresh={async () => {
              setLoadingAudit(true);
              try { setAuditLog(await api.adminAuditLog({ limit: 100 })); }
              catch (e) { toast.error(e.message); }
              finally { setLoadingAudit(false); }
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default AdminPage;
